import { useEffect, useState } from 'react'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

// Client-side cart / wishlist / viewer state for the results grid, mirroring ListingPage's
// List.jsx. On fast pages (SearchPagesController) this data is never in the SSR props - it is
// published client-side through window.pageConfig and the shared cart events, so the ModelCard's
// own add/remove handlers (App.events, /api/internal/*) work unchanged.
const useCartWishlist = () => {
  const [cart, setCart] = useState(EMPTY_OBJECT)
  const [cartItemIds, setCartItemIds] = useState(EMPTY_ARRAY)
  const [wishListItems, setWishListItems] = useState(EMPTY_ARRAY)
  const [currentUserId, setCurrentUserId] = useState(undefined)
  const [viewerIsAdult, setViewerIsAdult] = useState(false)
  const [showCgtVerification, setShowCgtVerification] = useState(false)

  useEffect(() => {
    const setCartInfo = (event) => {
      setCart(event.detail || EMPTY_OBJECT)
    }

    const readPageConfig = () => {
      setCurrentUserId(window?.pageConfig?.currentUserId || undefined)
      setViewerIsAdult(window?.pageConfig?.viewerIsAdult || false)
      setShowCgtVerification(window?.pageConfig?.showCgtVerification || false)
    }

    const removeCartItemId = () => {
      const currentCartItemIds = (window?.pageConfig?.cart_items || []).map(({ id }) => id)
      setCartItemIds(state => {
        const itemToBeRemoved = state.find(({ itemId }) => !currentCartItemIds.includes(itemId))
        return state.filter(({ itemId }) => itemId !== itemToBeRemoved?.itemId)
      })
    }

    window.addEventListener('navigation-cart-loaded', setCartInfo)
    window.addEventListener('window-config-loaded', readPageConfig)
    window.addEventListener('item-removed-from-cart', removeCartItemId)

    // React may initialize after the page config / cart already loaded; replay both events.
    if (window?.pageConfig) {
      readPageConfig()
      window.dispatchEvent(new CustomEvent('navigation-cart-loaded', {
        detail: window.pageConfig.cart,
      }))
    }

    return () => {
      window.removeEventListener('navigation-cart-loaded', setCartInfo)
      window.removeEventListener('window-config-loaded', readPageConfig)
      window.removeEventListener('item-removed-from-cart', removeCartItemId)
    }
  }, [])

  useEffect(() => {
    setCartItemIds(cart.cartItemIds || EMPTY_ARRAY)
  }, [cart])

  useEffect(() => {
    if (!currentUserId) {
      return undefined
    }

    let active = true
    const getWishListItems = async () => {
      try {
        const wishlist = await window.fetchJSON('/api/internal/wish-lists/default')
        const ids = (wishlist?.data || [])
          .map((item) => item?.attributes?.itemId)
          .filter(Boolean)
        if (active && ids.length > 0) {
          setWishListItems(ids)
        }
      } catch (error) {
        // Wishlist seeding is best-effort; add/remove still works without the initial list.
      }
    }

    getWishListItems()

    return () => {
      active = false
    }
  }, [currentUserId])

  return {
    cart,
    cartItemIds,
    setCartItemIds,
    wishListItems,
    setWishListItems,
    currentUserId,
    viewerIsAdult,
    setViewerIsAdult,
    showCgtVerification,
  }
}

export default useCartWishlist
